import { ReactNode } from 'react';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Stack, Text } from '@mantine/core';
import { FormProvider } from 'react-hook-form';
import { useCreateMasterDocumentDrawer } from './useCreateMasterDocumentDrawer.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { StyledDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { FileUpload } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FileUpload/FileUpload.tsx';
import { FileUploadPreview } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/FileUpload/FileUploadPreview.tsx';
import { MasterDocumentForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/MasterDocumentForm.tsx';
import { MASTER_DOCUMENT_FORM_FIELD } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/types.ts';
import { DocumentSourceToggle } from './DocumentSourceToggle.tsx';
import { TemplatePicker } from './TemplatePicker.tsx';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useMasterDocumentsScope } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/MasterDocumentsScopeContext.tsx';
import { useParams } from 'react-router-dom';

interface Props {
	opened: boolean;
	onClose: () => void;
	refreshList: () => void;
}

export const CreateMasterDocumentDrawer = ({ opened, onClose, refreshList }: Props) => {
	const tMipManage = useTranslationMipManage();

	const {
		isSubmitting,
		form,
		onSubmit,
		classifierOptions,
		uploadedFile,
		setUploadedFile,
		removeFile,
	} = useCreateMasterDocumentDrawer(refreshList, onClose);

	const { organizationId } = useMasterDocumentsScope();
	const { dealId, mipId } = useParams();

	const { value: documentManagedTemplatesEnabled } = useDealstackFeatureFlag(
		FEATURE_FLAG.DOCUMENT_MANAGED_TEMPLATES
	);
	// Same attributes the backend passes when it evaluates this flag on create, so a rule
	// targeting one deal, programme or organisation opens the upload here too.
	const { value: isDocxEnabled } = useDealstackFeatureFlag(FEATURE_FLAG.DOCX_MASTER_DOCUMENTS, {
		...(mipId && { mipId }),
		...(dealId && { dealId }),
		...(organizationId && { organizationId }),
	});
	const documentSource = form.watch(MASTER_DOCUMENT_FORM_FIELD.documentSource);
	const globalDocumentTemplateId = form.watch(
		MASTER_DOCUMENT_FORM_FIELD.globalDocumentTemplateId
	);

	let sourceContent: ReactNode;
	if (documentSource === 'template' && dealId) {
		sourceContent = (
			<TemplatePicker
				dealId={dealId}
				value={globalDocumentTemplateId}
				onChange={(next) =>
					form.setValue(
						MASTER_DOCUMENT_FORM_FIELD.globalDocumentTemplateId,
						next ?? undefined,
						{ shouldValidate: true }
					)
				}
			/>
		);
	} else if (!uploadedFile) {
		sourceContent = (
			<FileUpload
				setUploadedFile={setUploadedFile}
				inputId="masterDocumentFileUpload"
				allowDocx={isDocxEnabled}
			/>
		);
	} else {
		sourceContent = <FileUploadPreview fileName={uploadedFile.name} onRemove={removeFile} />;
	}

	return (
		<StyledDrawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={tMipManage('master_documents.text.add_doc')}
		>
			{isSubmitting && <InlineLoader />}
			<FormProvider {...form}>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						void form.handleSubmit(onSubmit())(e);
					}}
				>
					<MasterDocumentForm
						form={form}
						mode="create"
						classifierOptions={classifierOptions}
						disabled={isSubmitting}
						headerSlot={
							<Stack gap={8}>
								<Text size="sm" fw={600}>
									{tMipManage('master_documents.text.upload')}
								</Text>
								{/* Library availability is per deal — nothing to pick from at org level. */}
								{documentManagedTemplatesEnabled && !!dealId && (
									<DocumentSourceToggle
										value={documentSource}
										onChange={(next) =>
											form.setValue(
												MASTER_DOCUMENT_FORM_FIELD.documentSource,
												next,
												{ shouldValidate: true }
											)
										}
									/>
								)}
								{sourceContent}
							</Stack>
						}
						footerSlot={
							<Button
								fullWidth
								mt="auto"
								type="submit"
								loading={isSubmitting}
								disabled={isSubmitting}
							>
								{tMipManage('master_documents.text.add_doc_button')}
							</Button>
						}
					/>
				</form>
			</FormProvider>
		</StyledDrawer>
	);
};
